/**
 * monday's URL shapes, in one place (spec M0-F4 §7.1, §8).
 *
 * These strings are a **contract**, not a convenience: `Board.url`, `Item.url` and
 * `Item.relative_link` served by `/v2` are built from the same table, every migrated update,
 * e-mail and bookmark points at them, and getting one wrong silently breaks an integration.
 * The legacy `pulses` segment is monday's and is kept verbatim `[cap]`; `my_week` is kept as a
 * redirect only (M5.C3 — monday itself now 404s it).
 */

/** Every id segment is a decimal string (ADR-002 §2); anything else is a 404 with no request. */
export const ID_PATTERN = /^\d+$/;

export function isValidId(value: string | undefined | null): value is string {
  return typeof value === 'string' && ID_PATTERN.test(value);
}

export const routePaths = {
  home: '/',
  workspace: '/workspaces/:workspaceId',
  workspaceContent: '/workspaces/:workspaceId/content-management',
  board: '/boards/:boardId',
  boardView: '/boards/:boardId/views/:viewId',
  boardViewItem: '/boards/:boardId/views/:viewId/pulses/:itemId',
  boardItem: '/boards/:boardId/pulses/:itemId',
  /** P-325: monday's feed breadcrumb links a group here `[cap:notifications/11o-click-group]`. */
  boardGroup: '/boards/:boardId/groups/:groupId',
  boardActivityLog: '/boards/:boardId/views/:viewId/activity_log',
  /**
   * M5.C3. monday moved this: `/my_week` 404s in the 2026 layout and My Work is `/my_work`
   * (`discovery/ui-reference/my-work/01-my-work.json`). Ours serves the new path and keeps
   * `myWeekLegacy` registered as a redirect, so a migrated link still resolves.
   */
  myWork: '/my_work',
  myWeekLegacy: '/my_week',
  inbox: '/my_inbox',
  doc: '/docs/:docId',
  dashboard: '/dashboards/:dashboardId',
  admin: '/admin/*',
  notificationSettings: '/me/notifications',
} as const;

export function workspaceUrl(workspaceId: string): string {
  return `/workspaces/${workspaceId}`;
}

export function workspaceContentUrl(workspaceId: string): string {
  return `/workspaces/${workspaceId}/content-management`;
}

/** `Board.url` is `<PUBLIC_BASE_URL>` + this (`[09§A.2]`, `[cap]`). */
export function boardUrl(boardId: string): string {
  return `/boards/${boardId}`;
}

/**
 * A group's link, verbatim from monday's Update feed breadcrumb
 * (`discovery/ui-reference/notifications/11o-click-group.json`): `/boards/<b>/groups/<g>`, which
 * lands on the board. Group ids are monday's strings (`duplicate_of_open_requests`), so encoded.
 */
export function groupUrl(boardId: string, groupId: string): string {
  return `/boards/${boardId}/groups/${encodeURIComponent(groupId)}`;
}

export function boardViewUrl(boardId: string, viewId: string): string {
  return `/boards/${boardId}/views/${viewId}`;
}

/**
 * `Item.relative_link` (`[09§A.2]`) — the view-less form. The view-scoped form below is what
 * the shell pushes when an item is opened from a board, so the panel is linkable and survives
 * a reload with the right view underneath.
 */
export function itemRelativeLink(boardId: string, itemId: string): string {
  return `/boards/${boardId}/pulses/${itemId}`;
}

export function itemUrl(boardId: string, itemId: string): string {
  return itemRelativeLink(boardId, itemId);
}

export function itemInViewUrl(boardId: string, viewId: string, itemId: string): string {
  return `/boards/${boardId}/views/${viewId}/pulses/${itemId}`;
}

export function activityLogUrl(boardId: string, viewId: string): string {
  return `/boards/${boardId}/views/${viewId}/activity_log`;
}

export function docUrl(docId: string): string {
  return `/docs/${docId}`;
}

export function dashboardUrl(dashboardId: string): string {
  return `/dashboards/${dashboardId}`;
}

/** Absolute forms, for `/v2` and for anything that leaves the browser (e-mail, webhooks). */
export function absoluteUrl(baseUrl: string, relative: string): string {
  return `${baseUrl.replace(/\/+$/, '')}${relative}`;
}

export interface ShellEntity {
  id: string;
  type: 'board' | 'doc' | 'dashboard';
}

/** The link a sidebar row navigates to, per entity kind. */
export function entityUrl(entity: ShellEntity): string {
  switch (entity.type) {
    case 'doc':
      return docUrl(entity.id);
    case 'dashboard':
      return dashboardUrl(entity.id);
    default:
      return boardUrl(entity.id);
  }
}
